import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './TomaPedidos.css';
import API from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import {
  UtensilsCrossed,
  ShoppingBag,
  Home,
  Bike,
  Car,
  Users,
  Calendar,
  Clock,
  DollarSign,
  ArrowLeft,
  Check,
  CreditCard,
  X,
  Save,
  Printer,
  Edit3,
  Hash,
  UserCheck,
  MapPin,
  Phone,
  Trash2,
  RefreshCw,
  Unlock,
  Receipt
} from 'lucide-react';

/**
 * Componente header universal para todos los tipos de pedido
 * Maneja la información general, botones de acción y navegación
 */
const TomaPedidos = ({
  // Props de configuración del pedido
  tipoPedido = 'mesa', // 'mesa', 'llevar', 'domicilio', 'uber', 'rappi', 'delibot'

  // Props de información del pedido
  infoPedido = {},

  // Props de configuración de botones
  botonesConfig = {},

  // Props de callbacks
  onFinalizar = null,
  onActualizar = null,
  onRegresar = null,
  onAbrirCuenta = null,
  onReimprimirCuenta = null,
  onIrAPagar = null,
  onCambiarMesa = null,

  // Props adicionales
  loading = false,
  children
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { idMesa, idPedido } = useParams();

  // Estados locales
  const [fechaHora, setFechaHora] = useState(new Date());
  const [usuarioActual, setUsuarioActual] = useState(null);

  // Efecto para actualizar la fecha y hora cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setFechaHora(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Efecto para obtener usuario actual
  useEffect(() => {
    const usuario = API.auth.getCurrentUser();
    setUsuarioActual(usuario);
  }, []);

  // Configuración de títulos por tipo de pedido
  const titulosConfig = useMemo(() => ({
    mesa: 'Pedido a Mesa',
    llevar: 'Pedido Para Llevar',
    domicilio: 'Pedido a Domicilio',
    uber: 'Pedido Uber Eats',
    rappi: 'Pedido Rappi',
    delibot: 'Pedido Delibot'
  }), []);

  // Configuración de iconos por tipo de pedido
  const iconosConfig = useMemo(() => ({
    mesa: <UtensilsCrossed size={20} />,
    llevar: <ShoppingBag size={20} />,
    domicilio: <Home size={20} />,
    uber: <Bike size={20} />,
    rappi: <Bike size={20} />,
    delibot: <Car size={20} />
  }), []);

  // Configuración de colores por tipo de pedido
  const coloresConfig = useMemo(() => ({
    mesa: '#2196F3',
    llevar: '#4CAF50',
    domicilio: '#FF9800',
    uber: '#000000',
    rappi: '#FF6B35',
    delibot: '#9C27B0'
  }), []);

  // Configuración por defecto de botones
  const botonesDefecto = useMemo(() => ({
    finalizar: { visible: true, habilitado: true, texto: 'Finalizar' },
    actualizar: { visible: true, habilitado: true, texto: 'Actualizar' },
    regresar: { visible: true, habilitado: true, texto: 'Regresar' }
  }), []);

  // Combinar configuración de botones (merge por botón para no perder los campos
  // por defecto, como "texto", cuando botonesConfig solo manda visible/habilitado)
  const botones = useMemo(() => {
    const combinados = { ...botonesDefecto };
    Object.keys(botonesConfig).forEach((key) => {
      combinados[key] = { ...combinados[key], ...botonesConfig[key] };
    });
    return combinados;
  }, [botonesDefecto, botonesConfig]);

  // Información por defecto del pedido
  const infoDefecto = useMemo(() => ({
    numeroPedido: 'N/A',
    numeroMesa: null,
    nombreMesa: null,
    nombreCliente: null,
    telefono: null,
    direccion: null,
    mesero: null,
    numeroPersonas: 1,
    status: 'abierto',
    subtotal: 0,
    descuentos: 0,
    total: 0,
    fechaCreacion: new Date(),
    observaciones: null
  }), []);

  // Combinar información del pedido
  const info = useMemo(() => {
    const combined = {
      ...infoDefecto,
      ...infoPedido
    };
    console.log('🎉 TomaPedidos - info calculado:');
    console.log('  infoPedido:', infoPedido);
    console.log('  info.numeroPersonas:', combined.numeroPersonas);
    console.log('  info.mesa:', combined.mesa);
    console.log('  info.mesero:', combined.mesero);
    return combined;
  }, [infoDefecto, infoPedido]);


  // Formatear fecha y hora
  const formatearFecha = useCallback((fecha) => {
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const formatearHora = useCallback((fecha) => {
    return fecha.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, []);

  // Formatear moneda
  const formatearMoneda = useCallback((cantidad) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(cantidad || 0);
  }, []);

  // Manejadores de eventos
  const handleFinalizar = useCallback(() => {
    if (onFinalizar) {
      onFinalizar();
    } else {
      toast.info('Función finalizar no implementada');
    }
  }, [onFinalizar, toast]);

  const handleActualizar = useCallback(() => {
    if (onActualizar) {
      onActualizar();
    } else {
      toast.info('Función actualizar no implementada');
    }
  }, [onActualizar, toast]);

  const handleRegresar = useCallback(() => {
    if (onRegresar) {
      onRegresar();
    } else {
      navigate('/ventas');
    }
  }, [onRegresar, navigate]);

  const handleAbrirCuenta = useCallback(() => {
    if (onAbrirCuenta) {
      onAbrirCuenta();
    } else {
      toast.info('Función abrir cuenta no implementada');
    }
  }, [onAbrirCuenta, toast]);

  const handleReimprimirCuenta = useCallback(() => {
    if (onReimprimirCuenta) {
      onReimprimirCuenta();
    } else {
      toast.info('Función reimprimir cuenta no implementada');
    }
  }, [onReimprimirCuenta, toast]);

  const handleIrAPagar = useCallback(() => {
    if (onIrAPagar) {
      onIrAPagar();
    } else {
      toast.info('Función ir a pagar no implementada');
    }
  }, [onIrAPagar, toast]);

  const handleCambiarMesa = useCallback(() => {
    if (onCambiarMesa) {
      onCambiarMesa();
    } else if (info.onEditarMesa) {
      info.onEditarMesa();
    } else {
      // Funcionalidad por defecto: mostrar modal o navegar a selector de mesas
      toast.info('Para cambiar de mesa, implementa la función onCambiarMesa');
      console.log('Datos del pedido para cambio de mesa:', {
        idPedido: info.idPedido || idPedido,
        mesaActual: info.mesa,
        tipoPedido
      });
    }
  }, [onCambiarMesa, info.onEditarMesa, info.idPedido, info.mesa, idPedido, tipoPedido, toast]);


  // Renderizar información específica por tipo de pedido
  const renderInfoEspecifica = () => {
    switch (tipoPedido) {
      case 'mesa':
        return (
          <>
            {/* Card del tipo de pedido */}
            <div className="info-card tipo-pedido-card">
              <div className="info-card-icon tipo-pedido" style={{ backgroundColor: coloresConfig[tipoPedido] }}>
                {iconosConfig[tipoPedido]}
              </div>
              <div className="info-card-content">
                <div className="info-card-label-inline">
                  <span className="info-card-label">Tipo</span>
                  {info.numeroPedido !== 'N/A' && (
                    <span className="numero-pedido-inline">#{info.pedidoExistente.numeroDia}</span>
                  )}
                  {info.status === 'pendiente' && (
                    <span className="estado-pendiente-inline">⚠️</span>
                  )}
                </div>
                <span className="info-card-value">{titulosConfig[tipoPedido]}</span>
              </div>
            </div>

            {/* Card de Mesa - Siempre visible para pedidos de mesa */}
            <div
              className="info-card mesa-card clickable"
              onClick={handleCambiarMesa}
              title="Click para cambiar de mesa"
            >
              <div className="info-card-icon mesa">
                <UtensilsCrossed size={20} />
              </div>
              <div className="info-card-content">
                <span className="info-card-label">Mesa</span>
                <span className="info-card-value">
                  {info.mesa ?
                    `Mesa ${info.mesa.caption || info.mesa.numero || info.mesa.idMesa}` :
                    'Sin asignar'
                  }
                </span>
              </div>
              <Edit3 className="info-card-edit" size={16} />
            </div>

            {(info.mesero || info.onEditarMesero) && (
              <div
                className="info-card mesero-card"
                onClick={info.onEditarMesero}
                title="Click para cambiar mesero"
              >
                <div className="info-card-icon mesero">
                  <UserCheck size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Mesero</span>
                  <span className="info-card-value">
                    {info.mesero
                      ? (typeof info.mesero === 'object' && info.mesero.nombre ? info.mesero.nombre :
                        typeof info.mesero === 'string' ? info.mesero : 'Sin asignar')
                      : 'Sin asignar'
                    }
                  </span>
                </div>
                {info.onEditarMesero && (
                  <Edit3 className="info-card-edit" size={16} />
                )}
              </div>
            )}

            <div
              className="info-card personas-card"
              onClick={info.onEditarPersonas}
              title="Click para cambiar número de personas"
            >
              <div className="info-card-icon personas">
                <Users size={20} />
              </div>
              <div className="info-card-content">
                <span className="info-card-label">Personas</span>
                <span className="info-card-value">

                  {info.numeroPersonas || info.numeroPersonas === 0 ? info.numeroPersonas : 1}
                </span>
              </div>
              {info.onEditarPersonas && (
                <Edit3 className="info-card-edit" size={16} />
              )}
            </div>
          </>
        );

      case 'llevar':
        return (
          <>
            {/* Card del tipo de pedido */}
            <div className="info-card tipo-pedido-card">
              <div className="info-card-icon tipo-pedido" style={{ backgroundColor: coloresConfig[tipoPedido] }}>
                {iconosConfig[tipoPedido]}
              </div>
              <div className="info-card-content">
                <div className="info-card-label-inline">
                  <span className="info-card-label">Tipo</span>
                  {info.numeroPedido !== 'N/A' && (
                    <span className="numero-pedido-inline">#{info.numeroPedido}</span>
                  )}
                  {info.status === 'pendiente' && (
                    <span className="estado-pendiente-inline">⚠️</span>
                  )}
                </div>
                <span className="info-card-value">{titulosConfig[tipoPedido]}</span>
              </div>
            </div>

            {(info.nombreCliente || info.onEditarCliente) && (
              <div
                className={`info-card cliente-card ${info.onEditarCliente ? 'clickable' : ''}`}
                onClick={info.onEditarCliente}
                title={info.onEditarCliente ? "Click para editar cliente y teléfono" : ""}
              >
                <div className="info-card-icon cliente">
                  <UserCheck size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Cliente</span>
                  <span className="info-card-value">{info.nombreCliente || 'Sin asignar'}</span>
                </div>
                {info.onEditarCliente && (
                  <Edit3 className="info-card-edit" size={16} />
                )}
              </div>
            )}

            {(info.telefono || info.onEditarCliente) && (
              <div
                className={`info-card telefono-card ${info.onEditarCliente ? 'clickable' : ''}`}
                onClick={info.onEditarCliente}
                title={info.onEditarCliente ? "Click para editar cliente y teléfono" : ""}
              >
                <div className="info-card-icon telefono">
                  <Phone size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Teléfono</span>
                  <span className="info-card-value">{info.telefono || 'Sin asignar'}</span>
                </div>
                {info.onEditarCliente && (
                  <Edit3 className="info-card-edit" size={16} />
                )}
              </div>
            )}
          </>
        );

      case 'domicilio':
        return (
          <>
            {/* Card del tipo de pedido */}
            <div className="info-card tipo-pedido-card">
              <div className="info-card-icon tipo-pedido" style={{ backgroundColor: coloresConfig[tipoPedido] }}>
                {iconosConfig[tipoPedido]}
              </div>
              <div className="info-card-content">
                <div className="info-card-label-inline">
                  <span className="info-card-label">Tipo</span>
                  {info.numeroPedido !== 'N/A' && (
                    <span className="numero-pedido-inline">#{info.numeroPedido}</span>
                  )}
                  {info.status === 'pendiente' && (
                    <span className="estado-pendiente-inline">⚠️</span>
                  )}
                </div>
                <span className="info-card-value">{titulosConfig[tipoPedido]}</span>
              </div>
            </div>

            {/* Para pedidos a domicilio, mostrar cliente y teléfono juntos */}
            {info.nombreCliente && info.telefono && (
              <div className="info-card cliente-card">
                <div className="info-card-icon cliente">
                  <UserCheck size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Cliente</span>
                  <span className="info-card-value">{info.nombreCliente}</span>
                  <span className="info-card-telefono">
                    <Phone size={12} />
                    {info.telefono}
                  </span>
                </div>
              </div>
            )}

            {/* Fallback: mostrar por separado si solo hay uno de los dos */}
            {info.nombreCliente && !info.telefono && (
              <div className="info-card cliente-card">
                <div className="info-card-icon cliente">
                  <UserCheck size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Cliente</span>
                  <span className="info-card-value">{info.nombreCliente}</span>
                </div>
              </div>
            )}

            {info.telefono && !info.nombreCliente && (
              <div className="info-card telefono-card">
                <div className="info-card-icon telefono">
                  <Phone size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Teléfono</span>
                  <span className="info-card-value">{info.telefono}</span>
                </div>
              </div>
            )}

            {info.direccion && (
              <div className="info-card direccion-card">
                <div className="info-card-icon direccion">
                  <MapPin size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Dirección</span>
                  <span className="info-card-value">{info.direccion}</span>
                </div>
              </div>
            )}

            {(info.mesero || info.onEditarMesero) && (
              <div
                className="info-card mesero-card"
                onClick={info.onEditarMesero}
                title="Click para cambiar repartidor"
              >
                <div className="info-card-icon mesero">
                  <UserCheck size={20} />
                </div>
                <div className="info-card-content">
                  <span className="info-card-label">Repartidor</span>
                  <span className="info-card-value">

                    {info.mesero
                      ? (typeof info.mesero === 'object' && info.mesero.nombre ? info.mesero.nombre :
                        typeof info.mesero === 'string' ? info.mesero : 'Sin asignar')
                      : 'Sin asignar'
                    }
                  </span>
                </div>
                {info.onEditarMesero && (
                  <Edit3 className="info-card-edit" size={16} />
                )}
              </div>
            )}
          </>
        );

      default:
        return (
          <>
            {/* Card del tipo de pedido */}
            <div className="info-card tipo-pedido-card">
              <div className="info-card-icon tipo-pedido" style={{ backgroundColor: coloresConfig[tipoPedido] }}>
                {iconosConfig[tipoPedido]}
              </div>
              <div className="info-card-content">
                <div className="info-card-label-inline">
                  <span className="info-card-label">Tipo</span>
                  {info.numeroPedido !== 'N/A' && (
                    <span className="numero-pedido-inline">#{info.numeroPedido}</span>
                  )}
                  {info.status === 'pendiente' && (
                    <span className="estado-pendiente-inline">⚠️</span>
                  )}
                </div>
                <span className="info-card-value">{titulosConfig[tipoPedido]}</span>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="toma-pedidos-container">
      {/* Header Principal */}
      <div className="toma-pedidos-header-modern">
        <div className="header-content">

          {/* Fecha y Hora - Izquierda */}
          <div className="header-left-info">
            <div className="system-info-item date">
              <Calendar size={16} />
              <span>{formatearFecha(fechaHora)}</span>
            </div>
            <div className="header-separator"></div>
            <div className="system-info-item time">
              <Clock size={16} />
              <span className="time-display">{formatearHora(fechaHora)}</span>
            </div>
          </div>

          {/* Usuario - Derecha */}
          <div className="header-right-info">
            {/* Indicador de actualización */}
            {info.actualizandoEnSegundoPlano && (
              <div className="system-info-item updating" title="Actualizando pedido en segundo plano">
                <RefreshCw size={14} className="spinning" />
                <span>Actualizando...</span>
              </div>
            )}

            {usuarioActual && (
              <div className="user-info-badge">
                <UserCheck size={16} />
                <span>{usuarioActual.nombre || usuarioActual.usuario}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detalles del pedido */}
      <div className="pedido-detalles">
        {/* Información específica del tipo de pedido */}


        {/* Información del total 
        <div className="detalle-total">
          <div className="detail-icon">
            <DollarSign size={20} />
          </div>
          <div className="detail-info">
            <span className="detail-label">Total</span>
            <span className="detail-value total-amount">
              {formatearMoneda(info.total)}
            </span>
          </div>
        </div>
        */}
      </div>

      {/* Barra de información y acciones */}
      <div className="action-bar-modern">
        {/* Información específica del pedido */}
        <div className="pedido-info-cards">
          {renderInfoEspecifica()}
        </div>

        {/* Total del pedido */}
        {info.total > 0 && (
          <div className="total-display">
            <div className="total-label">Total</div>
            <div className="total-amount">{formatearMoneda(info.total)}</div>
          </div>
        )}

        {/* Separador */}
        <div className="action-bar-separator" />

        {/* Botones de acción */}
        <div className="action-buttons-group">

          {/* Botón Regresar */}
          {botones.regresar.visible && (
            <button
              className="action-btn secondary"
              onClick={handleRegresar}
              disabled={!botones.regresar.habilitado || loading}
              title="Regresar al listado anterior"
            >
              <ArrowLeft size={18} />
              <span>{botones.regresar.texto}</span>
            </button>
          )}


          {/* Botón Actualizar */}
          {botones.actualizar.visible && (
            <button
              className="action-btn info"
              onClick={handleActualizar}
              disabled={!botones.actualizar.habilitado || loading}
              title="Actualizar información del pedido"
            >
              <RefreshCw size={18} />
              <span>{botones.actualizar.texto}</span>
            </button>
          )}

          {/* Botón Abrir Cuenta */}
          {botones.abrirCuenta?.visible && (
            <button
              className="action-btn warning"
              onClick={handleAbrirCuenta}
              disabled={!botones.abrirCuenta.habilitado || loading}
              title="Abrir cuenta para seguir agregando productos"
            >
              <Unlock size={18} />
              <span>Abrir Cuenta</span>
            </button>
          )}

          {/* Botón Reimprimir Cuenta */}
          {botones.reimprimirCuenta?.visible && (
            <button
              className="action-btn info"
              onClick={handleReimprimirCuenta}
              disabled={!botones.reimprimirCuenta.habilitado || loading}
              title="Reimprimir ticket de la cuenta"
            >
              <Receipt size={18} />
              <span>Reimprimir Cuenta</span>
            </button>
          )}

          {/* Botón Ir a Pagar */}
          {botones.irAPagar?.visible && (
            <button
              className="action-btn success"
              onClick={handleIrAPagar}
              disabled={!botones.irAPagar.habilitado || loading}
              title="Proceder al pago de la cuenta"
            >
              <CreditCard size={18} />
              <span>Ir a Pagar</span>
            </button>
          )}

          {/* Botón Finalizar */}
          {botones.finalizar.visible && (
            <button
              className="action-btn success"
              onClick={handleFinalizar}
              disabled={!botones.finalizar.habilitado || loading}
              title="Finalizar y cerrar el pedido"
            >
              <Check size={18} />
              <span>{botones.finalizar.texto}</span>
            </button>
          )}
        </div>
      </div>

      {/* Contenido del componente hijo */}
      <div className="contenido-principal">
        {children}
      </div>


      {/* Overlay de carga */}
      {loading && (
        <div className="loading-overlay-modern">
          <div className="loading-content">
            <div className="loading-spinner-modern">
              <div className="spinner-circle"></div>
            </div>
            <span className="loading-text">Procesando...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TomaPedidos;